import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import api from '../api';

// Background images that rotate on refresh
const heroBackgrounds = [
  '/dallas1.png',
  '/dallas2.png',
  '/dallas3.png',
  '/country1.png',
];

function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Random background image on each render
  const heroBackground = useMemo(() => {
    return heroBackgrounds[Math.floor(Math.random() * heroBackgrounds.length)];
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await api.getNews();
        setNews(data);
      } catch (err) {
        console.error('Failed to fetch news:', err);
        setError('Failed to load news. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          pt: 16,
          pb: 8,
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("${heroBackground}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(26, 35, 126, 0.88) 0%, rgba(13, 27, 94, 0.92) 100%)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="overline"
            sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 2 }}
          >
            Updates
          </Typography>
          <Typography variant="h1" sx={{ mt: 1, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            News & Announcements
          </Typography>
        </Container>
      </Box>

      {/* News List */}
      <Box sx={{ py: 8, bgcolor: '#fafafa', minHeight: '50vh' }}>
        <Container maxWidth="lg">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : news.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h5" color="text.secondary">
                No news posts yet. Check back soon!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {news.map((post) => (
                <Grid item xs={12} md={6} lg={4} key={post.id}>
                  <Card
                    component={Link}
                    to={`/news/${post.slug}`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    {(post.imageUrl || post.videoUrl) && (
                      <Box
                        sx={{
                          height: 220,
                          backgroundImage: `url(${post.imageUrl || (post.videoUrl && `https://img.youtube.com/vi/${post.videoUrl.match(/(?:v=|youtu\.be\/)([^&\s]+)/)?.[1]}/hqdefault.jpg`)})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          position: 'relative',
                        }}
                      >
                        {post.videoUrl && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              opacity: 0.9,
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                            }}
                          >
                            <PlayCircleFilledIcon sx={{ fontSize: 64 }} />
                          </Box>
                        )}
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {new Date(post.publishedAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 1, mb: 2 }}>
                        {post.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {post.excerpt}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
}

export default News;
